import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { chatService, chatServiceCharacter } from '../services/chatService';
import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import api from '../services/api';
import { booksAPI } from '../services/api';
import { reviewAPI } from '../services/api';

const BookDetailPage = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chat, setChat] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [characterChat, setCharacterChat] = useState(null);
  const [characterChatHistory, setCharacterChatHistory] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showCharacterDropdown, setShowCharacterDropdown] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(null);

  // Fetch book details and initialize chat
  useEffect(() => {
    const fetchBook = async () => {
      console.log('Fetching book details for ID:', bookId);
      try {
        const bookDoc = await getDoc(doc(db, 'books', bookId));
        console.log('Book document retrieved:', bookDoc.exists());
        
        if (bookDoc.exists()) {
          const bookData = bookDoc.data();
          console.log('Book data:', {
            title: bookData.title,
            hasCoverImage: !!bookData.coverImageUrl,
            hasTextContent: !!bookData.textContent
          });
          
          if (!bookData.coverImageUrl || !bookData.textContent) {
            console.error('Book is missing required data:', {
              missingCover: !bookData.coverImageUrl,
              missingText: !bookData.textContent
            });
            setError('This book is not fully uploaded yet. Please try again later.');
            setLoading(false);
            return;
          }

          // Fetch userBooks document for this user and book
          let userBookRead = false;
          let userBookLastReadAt = null;
          if (currentUser) {
            const userBookRef = doc(db, 'userBooks', `${currentUser.uid}_${bookId}`);
            const userBookDoc = await getDoc(userBookRef);
            if (userBookDoc.exists()) {
              const userBookData = userBookDoc.data();
              userBookRead = !!userBookData.read;
              userBookLastReadAt = userBookData.lastReadAt || null;
            }
          }

          setBook({
            ...bookData,
            read: userBookRead,
            lastReadAt: userBookLastReadAt
          });
          console.log('Book state updated');

          // Initialize chat with book content
          console.log('Initializing chat with book content');
          const newChat = await chatService.startChat(bookId, bookData.textContent, currentUser.uid);
          setChat(newChat);
          console.log('Chat initialized');

          // Set up chat history listener
          console.log('Setting up chat history listener');
          const unsubscribe = chatService.setupChatListener(currentUser.uid, bookId, (messages) => {
            console.log('Chat history updated with', messages.length, 'messages');
            // Filter out system messages, initial context messages, and the first assistant greeting
            const filteredMessages = messages.filter(msg => 
              msg.role !== 'system' && 
              !msg.isInitialContext && 
              !(msg.role === 'assistant' && msg.content.includes("I understand. I will help you with any questions"))
            );
            setChatHistory(filteredMessages);
          });
          
          setLoading(false);
          return unsubscribe;
        } else {
          console.error('Book not found in database');
          setError('Book not found');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching book:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        setError('Error loading book details');
        setLoading(false);
      }
    };

    const unsubscribe = fetchBook();
    
    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe.then(cleanup => cleanup && cleanup());
      }
    };
  }, [bookId]);

  // Fetch reviews for this book
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const data = await reviewAPI.getReviews(bookId);
        setReviews(data);
        if (data.length > 0) {
          const avg = data.reduce((sum, r) => sum + (r.rating || 0), 0) / data.length;
          setAvgRating(avg);
        } else {
          setAvgRating(null);
        }
      } catch (e) {
        setReviews([]);
        setAvgRating(null);
      }
    };
    fetchReviews();
  }, [bookId]);

  // Handler to start character chat
  const handleStartCharacterChat = async (character) => {
    if (!book || !currentUser) return;
    setSelectedCharacter(character);
    setShowCharacterDropdown(false);
    setLoading(true);
    try {
      const newCharChat = await chatServiceCharacter.startChat(
        bookId,
        book.textContent,
        currentUser.uid,
        character,
        book.title
      );
      setCharacterChat(newCharChat);
      // Set up chat history listener
      const unsubscribe = chatServiceCharacter.setupChatListener(
        currentUser.uid,
        bookId,
        character,
        (messages) => {
          setCharacterChatHistory(messages.filter(msg => !msg.isInitialContext));
        }
      );
      setLoading(false);
      return unsubscribe;
    } catch (err) {
      setError('Failed to start character chat');
      setLoading(false);
    }
  };

  // Handler to switch back to book chat
  const handleSwitchToBookChat = () => {
    setSelectedCharacter(null);
    setCharacterChat(null);
    setCharacterChatHistory([]);
  };

  // Handler to send message (book or character chat)
  const handleSendMessageUnified = async () => {
    if (!message.trim() || !currentUser) return;
    setSending(true);
    setError(null);
    try {
      if (selectedCharacter && characterChat) {
        await chatServiceCharacter.sendMessage(
          characterChat.id,
          message,
          currentUser.uid,
          selectedCharacter,
          book.title
        );
        setMessage('');
      } else if (chat) {
        await chatService.sendMessage(chat.id, message, currentUser.uid);
        setMessage('');
      }
    } catch (error) {
      setError('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsRead = async (e) => {
    try {
      const isRead = e.target.checked;
      // Only update userBooks, not books collection
      if (!currentUser) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const userBookRef = doc(db, 'userBooks', `${currentUser.uid}_${bookId}`);
      if (isRead) {
        // Mark as read: set or update userBooks doc
        await setDoc(userBookRef, {
          userId: currentUser.uid,
          bookId,
          read: true,
          lastReadAt: new Date()
        }, { merge: true });
      } else {
        // Unmark as read: update userBooks doc
        await setDoc(userBookRef, {
          userId: currentUser.uid,
          bookId,
          read: false,
          lastReadAt: null
        }, { merge: true });
      }
      setBook(prev => ({
        ...prev,
        read: isRead,
        lastReadAt: isRead ? new Date() : null
      }));
      setError(null);
    } catch (error) {
      console.error('Error updating read status:', error);
      setError(error.message || 'Failed to update read status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{error}</h2>
        <Button variant="primary" onClick={() => navigate('/books')}>
          Back to Books
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back Button */}
          <div className="mb-6">
            <Button
              variant="secondary"
              onClick={() => navigate('/books')}
              className="flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back to Books
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Book Information */}
            <div className="lg:col-span-1">
              <Card className="p-6">
                <div className="flex flex-col items-center mb-6">
                  <img 
                    src={book.coverImageUrl} 
                    alt={book.title} 
                    className="w-48 h-auto rounded-lg shadow-md mb-4"
                  />
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-800">{book.title}</h1>
                    <p className="text-gray-600">by {book.author}</p>
                    <div className="flex items-center justify-center mt-2">
                      <span className="bg-secondary-orange text-white text-xs font-bold px-2 py-1 rounded-full mr-2">
                        {book.category}
                      </span>
                      <span className="bg-gray-200 text-gray-700 text-xs font-bold px-2 py-1 rounded-full">
                        {book.genre}
                      </span>
                    </div>
                    {/* Average Rating */}
                    <div className="mt-4 flex flex-col items-center">
                      <div className="flex items-center mb-2">
                        <span className="text-yellow-500 text-lg mr-1">★</span>
                        <span className="text-lg font-semibold">{avgRating ? avgRating.toFixed(1) : 'No ratings yet'}</span>
                        {reviews.length > 0 && (
                          <span className="ml-2 text-gray-500 text-sm">({reviews.length} review{reviews.length > 1 ? 's' : ''})</span>
                        )}
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => navigate(`/books/${bookId}/review`)}
                        className="mt-2"
                      >
                        Write Review
                      </Button>
                    </div>
                    <div className="mt-4 flex items-center justify-center">
                      <input
                        type="checkbox"
                        id="readStatus"
                        checked={book?.read || false}
                        onChange={handleMarkAsRead}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor="readStatus" className="ml-2 text-sm text-gray-700">
                        Mark as read
                      </label>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Chat Section (Book or Character) */}
            <div className="lg:col-span-2">
              <Card className="p-6">
                {/* Character Chat Controls (inside chat card, above messages/input) */}
                {book?.characterList && book.characterList.length > 0 && (
                  <div className="mb-4 flex flex-col items-center relative">
                    {!selectedCharacter ? (
                      <>
                        <div className="w-full flex justify-center">
                          <Button
                            variant="primary"
                            onClick={() => setShowCharacterDropdown((v) => !v)}
                            className="mb-2"
                          >
                            Talk to a Character
                          </Button>
                        </div>
                        {showCharacterDropdown && (
                          <div className="absolute top-12 left-1/2 transform -translate-x-1/2 w-64 z-20">
                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg">
                              {book.characterList.map((char) => (
                                <button
                                  key={char}
                                  className="block w-full text-left px-4 py-2 hover:bg-primary hover:text-white transition-colors rounded"
                                  onClick={() => handleStartCharacterChat(char)}
                                >
                                  {char}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col items-center">
                        <div className="mb-2 text-lg font-semibold text-primary">Talking to: {selectedCharacter}</div>
                        <Button variant="secondary" onClick={handleSwitchToBookChat} className="mb-2">
                          Switch to Book Chat
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {/* Chat Title/Description */}
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-2">
                    {selectedCharacter ? `Chat with ${selectedCharacter}` : 'Chat with the Book'}
                  </h2>
                  <p className="text-gray-600">
                    {selectedCharacter
                      ? `Ask questions to ${selectedCharacter} from "${book.title}" and get AI-powered responses as that character.`
                      : `Ask questions about "${book.title}" and get AI-powered responses based on the book's content.`}
                  </p>
                </div>
                {/* Chat Messages */}
                <div className="h-[500px] overflow-y-auto mb-4 p-4 bg-gray-50 rounded-lg">
                  {(selectedCharacter ? characterChatHistory : chatHistory).length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500 text-sm">
                        Start a conversation {selectedCharacter ? `with ${selectedCharacter}` : 'about the book'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {(selectedCharacter ? characterChatHistory : chatHistory).map((msg, index) => (
                        <div key={index} className="space-y-2">
                          {msg.role === 'user' ? (
                            <div className="flex justify-end">
                              <div className="bg-primary text-white rounded-lg px-4 py-2 max-w-[80%]">
                                <p className="text-sm">{msg.content}</p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-start">
                              <div className="bg-gray-200 text-gray-800 rounded-lg px-4 py-2 max-w-[80%]">
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
                {/* Message Input */}
                <div className="flex gap-2">
                  <Input
                    type="expandable-text"
                    placeholder={selectedCharacter ? `Ask ${selectedCharacter}...` : 'Ask a question about the book...'}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessageUnified()}
                    className="flex-grow"
                    disabled={sending}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSendMessageUnified}
                    disabled={sending}
                  >
                    {sending ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      'Send'
                    )}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookDetailPage; 